import { PluginObject } from "vue";
import Keycloak from "keycloak-js";

import router from "@/lib/router";
import store from "@/lib/vuex";

import { configuration } from "@/configuration";

import i18n from "../i18n";

import loginModule from "./store";
import apiModule from "./api/Store";
import { EmaSoftsToken } from "./typings/EmaSoftsToken";
import { hub, replicable } from "../axios";

i18n.mergeLocaleMessage("fr", {
	"page.not.authorized": "Vous n'êtes pas autorisé à visiter cette page"
});

export const keycloak: Keycloak = new Keycloak(configuration.keycloakConfig);

function saveTokens() {
	if (keycloak.token) {
		sessionStorage.setItem("token", keycloak.token);
	}

	if (keycloak.idToken) {
		sessionStorage.setItem("idToken", keycloak.idToken);
	}

	if (keycloak.refreshToken) {
		sessionStorage.setItem("refreshToken", keycloak.refreshToken);
	}

	store.commit("login/setAuthenticated", true);

	keycloak.loadUserProfile().then(profile => {
		profile.id = keycloak.subject;
		store.commit("login/setCurrentUser", profile);
	});

	// @ts-ignore
	const hubClientId = configuration.backend.hub.clientId;
	// @ts-ignore
	const replicableClientId = configuration.backend.replicable.clientId;

	const hubResourceAccess = keycloak.resourceAccess && keycloak.resourceAccess[hubClientId];
	const replicableResourceAccess = keycloak.resourceAccess && keycloak.resourceAccess[replicableClientId];

	if (hubResourceAccess || replicableResourceAccess) {
		const hubRoles = hubResourceAccess?.roles || [];
		const replicableRoles = replicableResourceAccess?.roles || [];
		const roles = [...hubRoles, ...replicableRoles];
		store.commit("login/setRoles", roles);
		store.commit("login/setToken", keycloak.tokenParsed as EmaSoftsToken);
	}
}

function clearTokens() {
	sessionStorage.removeItem("token");
	sessionStorage.removeItem("idToken");
	sessionStorage.removeItem("refreshToken");

	store.commit("login/setCurrentUser", undefined);
	store.commit("login/setAuthenticated", false);
	store.commit("login/setRoles", []);
	store.commit("login/setToken", undefined);
}

function buildCallbackUrl(login: boolean, path: string) {
	if (!path) {
		path = "/";
	}

	return (
		// @ts-ignore
		window.location.origin + `/#/${login ? "login" : "logout"}/callback?path=${encodeURIComponent(path)}`
	);
}

function authenticate(callbackPath: string) {
	return new Promise<void>(async resolve => {
		// On vérifie s'il est authentifié, ça n'est pas le cas à la première arrivée sur une page.
		if (!keycloak.authenticated) {
			store.commit("login/setAuthenticating", true);
			await keycloak.login({
				redirectUri: buildCallbackUrl(true, callbackPath)
			});
			return;
		}

		// On vérifie que le token n'est pas expiré sinon on le renouvèle
		if (!keycloak.isTokenExpired(5)) {
			resolve();
			return;
		}

		try {
			const refreshed = await keycloak.updateToken(5);

			if (refreshed) {
				saveTokens();
				resolve();
			} else {
				clearTokens();
				await keycloak.login({
					redirectUri: buildCallbackUrl(true, callbackPath)
				});
			}
		} catch (e) {
			clearTokens();
			await keycloak.login({
				redirectUri: buildCallbackUrl(true, callbackPath)
			});
		}
	});
}

export async function init() {
	try {
		const initConfig = {
			checkLoginIframe: false
		} as Keycloak.KeycloakInitOptions;

		const token = sessionStorage.getItem("token");
		if (token) {
			initConfig.token = token;
		}
		const idToken = sessionStorage.getItem("idToken");
		if (idToken) {
			initConfig.idToken = idToken;
		}
		const refreshToken = sessionStorage.getItem("refreshToken");
		if (refreshToken) {
			initConfig.refreshToken = refreshToken;
		}

		store.commit("login/setAuthenticating", true);
		await keycloak.init(initConfig);
	} catch (e) {
		// tslint:disable-next-line
		console.error("Error during init keycloak", e);
	} finally {
		store.commit("login/setAuthenticating", false);
	}
}

export default {
	install() {
		for (const axios of [hub, replicable]) {
			if (axios.defaults.transformRequest instanceof Array) {
				axios.defaults.transformRequest.push((data: any, headers: any) => {
					const cabinets = store.getters["login/cabinets"];

					if (cabinets?.length > 1) {
						const cabinetSelected = store.getters["login/cabinet"];
						headers["Cabinet-Id"] = cabinetSelected?.id ?? "";
					}

					return data;
				});
			}
		}

		keycloak.onAuthSuccess = saveTokens;
		keycloak.onAuthLogout = clearTokens;
		keycloak.onAuthError = clearTokens;
		keycloak.onTokenExpired = async () => {
			try {
				const refreshed = await keycloak.updateToken(5);

				if (!refreshed) {
					clearTokens();
					await router.push({ path: "/login", query: { path: router.currentRoute.path } });
				}
			} catch (e) {
				clearTokens();
				await router.push({ path: "/login", query: { path: router.currentRoute.path } });
			}
		};

		store.registerModule("login", loginModule);
		store.registerModule("keycloak-api", apiModule);

		/*
		 * Cette route permet de sécuriser les routes ayant comme meta.secured = true
		 */
		router.beforeEach(async (to, from, next) => {
			if (to.path === "/login") {
				let path = to.query.path;

				if (path === "/login/callback" || path === "/logout/callback") {
					path = "/";
				}

				store.commit("login/setAuthenticating", true);
				await keycloak.login({ redirectUri: buildCallbackUrl(true, path as string) });
			} else if (to.path === "/logout") {
				let path = to.query.path;

				if (path === "/login/callback" || path === "/logout/callback") {
					path = "/";
				}

				clearTokens();
			} else if (to.path === "/login/callback") {
				let route = "/";

				if (to.query.path) {
					const resolution = router.resolve(decodeURIComponent(to.query.path as string));

					if (resolution.resolved.matched.length > 0) {
						route = to.query.path as string;
					}
				}

				try {
					await authenticate(route);
				} finally {
					store.commit("login/setAuthenticating", false);
				}

				next(route);
			} else if (to.path === "/account") {
				await keycloak.accountManagement();
			} else if (to.meta && to.meta.secured) {
				try {
					await authenticate(to.fullPath);
				} finally {
					store.commit("login/setAuthenticating", false);
				}

				if (to.meta.roles) {
					if (store.getters["login/hasRoles"](to.meta.roles)) {
						next();
					} else {
						next("/errors/notAuthorized");
					}
				} else {
					next();
				}
			} else {
				next();
			}
		});
	}
} as PluginObject<any>;
